import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Eraser, X } from "lucide-react";

const INK = "#0a1a4a";
const FONT = '"Dancing Script", cursive';

async function typedToPng(text, size) {
  try { await document.fonts.load(`${size}px ${FONT}`); } catch { /* fallback font */ }
  const c = document.createElement("canvas");
  const ctx = c.getContext("2d");
  ctx.font = `${size}px ${FONT}`;
  c.width = Math.ceil(ctx.measureText(text).width + 40);
  c.height = Math.ceil(size * 1.6);
  ctx.font = `${size}px ${FONT}`;
  ctx.fillStyle = INK;
  ctx.textBaseline = "middle";
  ctx.fillText(text, 20, c.height / 2);
  return c.toDataURL("image/png");
}

function DrawPad({ canvasRef, onInk }) {
  const drawing = useRef(false);
  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 3.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = INK;
  }, [canvasRef]);
  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * canvasRef.current.width, ((e.clientY - r.top) / r.height) * canvasRef.current.height];
  };
  const down = (e) => { drawing.current = true; const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(...pos(e)); canvasRef.current.setPointerCapture(e.pointerId); };
  const move = (e) => { if (!drawing.current) return; const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(...pos(e)); ctx.stroke(); onInk(); };
  return (
    <canvas ref={canvasRef} width={600} height={180} onPointerDown={down} onPointerMove={move} onPointerUp={() => { drawing.current = false; }}
      className="w-full bg-white touch-none cursor-crosshair border-b-2 border-black/20" data-testid="signature-canvas" />
  );
}

export default function SignatureModal({ kind, defaultText, onClose, onAdopt }) {
  const [tab, setTab] = useState("type");
  const [text, setText] = useState(defaultText || "");
  const [hasInk, setHasInk] = useState(false);
  const canvasRef = useRef(null);
  const label = kind === "initials" ? "Initials" : "Signature";

  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasInk(false); };
  const adopt = async () => {
    if (tab === "draw") {
      if (!hasInk) return toast.error(`Draw your ${label.toLowerCase()} first`);
      return onAdopt(canvasRef.current.toDataURL("image/png"));
    }
    if (!text.trim()) return toast.error(`Type your ${label.toLowerCase()} first`);
    onAdopt(await typedToPng(text.trim(), kind === "initials" ? 64 : 72));
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="signature-modal">
      <div className="w-full max-w-xl bg-[var(--ff-surface)] border border-[var(--ff-border)] p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="font-display text-2xl">Adopt Your {label}</div>
          <button onClick={onClose} className="text-[var(--ff-text-dim)] hover:text-white" data-testid="signature-modal-close"><X size={18} /></button>
        </div>
        <div className="flex gap-px bg-[var(--ff-border)] w-fit">
          {["type", "draw"].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 font-mono-ui text-xs uppercase tracking-[0.15em] ${tab === t ? "bg-[var(--ff-red)] text-white" : "bg-[var(--ff-bg)] text-[var(--ff-text-dim)]"}`} data-testid={`signature-tab-${t}`}>{t}</button>
          ))}
        </div>
        {tab === "type" ? (
          <div className="space-y-3">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder={kind === "initials" ? "Your initials" : "Your full name"} className="w-full px-3 py-2 text-sm" data-testid="signature-type-input" />
            <div className="bg-white h-28 flex items-center px-6 overflow-hidden" style={{ fontFamily: FONT, color: INK, fontSize: kind === "initials" ? 44 : 48 }} data-testid="signature-type-preview">{text || " "}</div>
          </div>
        ) : (
          <div className="space-y-2">
            <DrawPad canvasRef={canvasRef} onInk={() => setHasInk(true)} />
            <button onClick={clear} className="btn-ghost inline-flex items-center gap-1.5 text-[0.65rem] !px-3 !py-1.5" data-testid="signature-clear"><Eraser size={11} /> Clear</button>
          </div>
        )}
        <p className="text-[0.7rem] text-[var(--ff-text-dim)]">By adopting, you agree this {label.toLowerCase()} is the electronic representation of your signature for this contract.</p>
        <button onClick={adopt} className="btn-primary w-full justify-center" data-testid="signature-adopt-btn">Adopt {label}</button>
      </div>
    </div>
  );
}
